import { createContext, useContext, type ReactNode } from 'react';
import {
  buildEmployeeIdentityIndex, type EmployeeIdentity, type EmployeeIdentityIndex,
} from '../utils/people/peopleDirectory';

export type PeopleDirectory = {
  employees: EmployeeIdentityIndex;
  photoForUser: (userId?: string | null) => string | null;
  /** Rows that only know an employee id/code call this; it loads /employeesAll once. */
  requestEmployees: () => void;
};

export type PeopleDirectorySeed = {
  employees?: readonly EmployeeIdentity[];
  photoByUserId?: Readonly<Record<string, string>>;
};

export function photoLookup(photos: ReadonlyMap<string, string>) {
  return (userId?: string | null) => {
    const key = userId?.toString().trim();
    return key ? photos.get(key) ?? null : null;
  };
}

/** A fixed directory — the default outside the provider, and test fixtures. */
export function createPeopleDirectory({ employees, photoByUserId }: PeopleDirectorySeed = {}): PeopleDirectory {
  return {
    employees: buildEmployeeIdentityIndex(employees),
    photoForUser: photoLookup(new Map(Object.entries(photoByUserId ?? {}))),
    requestEmployees: () => undefined,
  };
}

// Outside the provider (tests, unmounted trees) nothing is fetched: rows fall
// back to their own photo or initials. This module imports no auth/API code,
// so a row never pulls those into its import graph.
export const PeopleDirectoryContext = createContext<PeopleDirectory>(createPeopleDirectory());

export function StaticPeopleDirectory({ children, directory }: { children: ReactNode; directory: PeopleDirectory }) {
  return <PeopleDirectoryContext.Provider value={directory}>{children}</PeopleDirectoryContext.Provider>;
}

export function usePeopleDirectory() {
  return useContext(PeopleDirectoryContext);
}
